/**
 * Export Excel riche et formaté du rapport financier.
 *
 * Génère un classeur avec six feuilles :
 *  - « Résumé »              : KPI financiers + répartition des dépenses.
 *  - « Cotisations »         : historique complet (statut, mode, validation).
 *  - « Dépenses »            : registre des dépenses par catégorie.
 *  - « Recettes par type »   : validé / en attente par type de contribution.
 *  - « Par membre »          : total validé / en attente par contributeur.
 *  - « Évolution mensuelle » : recettes / dépenses / solde sur 12 mois.
 *
 * Le formatage (couleurs, gras, bordures, formats nombre) repose sur ExcelJS,
 * la version communautaire de SheetJS (`xlsx`) ne sachant pas styliser.
 */

// ---- Palette (partagée avec membersExcel) ----
const NAVY = 'FF0B1F3A';
const GREEN = 'FF0F5132';
const GREEN_LIGHT = 'FFE7F1EC';
const RED = 'FFB42318';
const RED_LIGHT = 'FFFBE9E7';
const AMBER = 'FF9A6700';
const BLUE = 'FF1D4ED8';
const SLATE = 'FF475467';
const ZEBRA = 'FFF5F7FA';
const GREY_BORDER = 'FFD9DEE5';

const MONEY_FMT = '#,##0" FCFA"';
const PCT_FMT = '0.0"%"';

const thin = { style: 'thin', color: { argb: GREY_BORDER } };
const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

const PAYMENT_LABELS = { wave: 'Wave', orange_money: 'Orange Money', cash: 'Espèces' };
const STATUS_LABELS = { confirmed: 'Validée', rejected: 'Rejetée' };
const STATUS_COLORS = { confirmed: GREEN, rejected: RED };

const fmtDate = (v) => {
  if (!v) return '';
  const d = v.toDate ? v.toDate() : new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('fr-FR');
};

/**
 * Construit le classeur financier et déclenche son téléchargement.
 * @param {object} data
 * @param {object} data.stats               KPI agrégés.
 * @param {Array}  data.allContributions    Toutes les cotisations.
 * @param {Array}  data.expenses            Dépenses.
 * @param {Array}  data.monthlyData         Évolution mensuelle ({name, year, income, expense}).
 * @param {Array}  data.expensesByCategory  Répartition ({name, value}).
 * @param {Array}  data.expenseCategories   Référentiel des catégories ({value, label}).
 */
export async function exportFinancesToExcel({
  stats,
  allContributions = [],
  expenses = [],
  monthlyData = [],
  expensesByCategory = [],
  expenseCategories = [],
  typeBreakdown = [],
  memberBreakdown = []
}) {
  // exceljs est volumineux : chargé à la demande pour rester hors du bundle principal.
  const ExcelJS = (await import('exceljs')).default;
  const catLabel = (value) => expenseCategories.find((c) => c.value === value)?.label || value || 'Autre';

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Daara Wakana';
  wb.created = new Date();

  buildSummarySheet(wb, { stats, expensesByCategory });
  buildContributionsSheet(wb, allContributions);
  buildExpensesSheet(wb, expenses, catLabel);
  if (typeBreakdown.length) buildTypeSheet(wb, typeBreakdown, stats);
  if (memberBreakdown.length) buildMembersSheet(wb, memberBreakdown);
  buildMonthlySheet(wb, monthlyData);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport_financier_daara_wakana_${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---- Primitives de mise en forme ----

function titleBanner(ws, lastCol, title, subtitle) {
  ws.mergeCells(1, 1, 1, lastCol);
  const t = ws.getCell(1, 1);
  t.value = title;
  t.font = { size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
  t.alignment = { vertical: 'middle', indent: 1 };
  ws.getRow(1).height = 30;

  ws.mergeCells(2, 1, 2, lastCol);
  const s = ws.getCell(2, 1);
  s.value = subtitle;
  s.font = { size: 11, color: { argb: 'FFC7D2E0' } };
  s.alignment = { vertical: 'middle', indent: 1 };
  ws.getRow(2).height = 20;

  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= lastCol; c++) {
      ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    }
  }
  ws.getRow(3).height = 6;
}

function tableHeader(ws, rowIdx, headers, fill = GREEN) {
  const row = ws.getRow(rowIdx);
  headers.forEach((h, i) => {
    const cell = row.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = allBorders;
  });
  row.height = 24;
}

// ---- Feuille « Résumé » ----

function buildSummarySheet(wb, { stats, expensesByCategory }) {
  const ws = wb.addWorksheet('Résumé', { pageSetup: { orientation: 'portrait' } });
  ws.columns = [{ width: 32 }, { width: 22 }, { width: 16 }];

  titleBanner(
    ws,
    3,
    'Rapport financier — Daara Wakana',
    `Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
  );

  let r = 4;
  const sectionHeader = (label) => {
    ws.mergeCells(r, 1, r, 3);
    const cell = ws.getCell(r, 1);
    cell.value = label;
    cell.font = { bold: true, size: 12, color: { argb: GREEN } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    cell.alignment = { vertical: 'middle', indent: 1 };
    ws.getRow(r).height = 22;
    r++;
  };
  const kv = (label, value, { money = false, color } = {}) => {
    const l = ws.getCell(r, 1);
    l.value = label;
    l.font = { color: { argb: SLATE } };
    l.border = { bottom: thin };
    const v = ws.getCell(r, 2);
    v.value = value;
    v.font = { bold: true, color: color ? { argb: color } : undefined };
    if (money) v.numFmt = MONEY_FMT;
    v.border = { bottom: thin };
    r++;
  };

  sectionHeader('Résumé financier');
  kv('Solde total', stats.balance, { money: true, color: stats.balance >= 0 ? GREEN : RED });
  kv('Total recettes', stats.totalIncome, { money: true, color: GREEN });
  kv('Total dépenses', stats.totalExpenses, { money: true, color: RED });
  kv('Recettes du mois', stats.monthlyIncome, { money: true });
  kv('Dépenses du mois', stats.monthlyExpenses, { money: true });
  kv('Cotisations en attente', stats.pendingCount);
  kv('Montant en attente', stats.pendingAmount, { money: true, color: AMBER });

  r++;
  sectionHeader('Répartition des dépenses par catégorie');
  tableHeader(ws, r, ['Catégorie', 'Montant', '% du total'], SLATE);
  r++;
  const sorted = [...expensesByCategory].sort((a, b) => b.value - a.value);
  sorted.forEach((c) => {
    const pct = stats.totalExpenses > 0 ? (c.value / stats.totalExpenses) * 100 : 0;
    const row = ws.getRow(r);
    row.getCell(1).value = c.name;
    row.getCell(2).value = c.value;
    row.getCell(3).value = pct;
    row.getCell(2).numFmt = MONEY_FMT;
    row.getCell(3).numFmt = PCT_FMT;
    row.getCell(3).alignment = { horizontal: 'center' };
    for (let c2 = 1; c2 <= 3; c2++) row.getCell(c2).border = allBorders;
    row.getCell(1).alignment = { indent: 1 };
    r++;
  });
  if (sorted.length > 0) {
    const row = ws.getRow(r);
    row.getCell(1).value = 'TOTAL';
    row.getCell(2).value = stats.totalExpenses;
    row.getCell(2).numFmt = MONEY_FMT;
    for (let c2 = 1; c2 <= 3; c2++) {
      row.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RED_LIGHT } };
      row.getCell(c2).font = { bold: true, color: { argb: RED } };
      row.getCell(c2).border = allBorders;
    }
  }
}

// ---- Feuille « Cotisations » ----

function buildContributionsSheet(wb, contributions) {
  const ws = wb.addWorksheet('Cotisations', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  const headers = ['Date', 'Membre', 'Type', 'Mode de paiement', 'Montant', 'Statut', 'Validé par', 'Date validation'];
  const widths = [14, 24, 18, 18, 16, 14, 20, 16];
  ws.columns = widths.map((w) => ({ width: w }));
  const lastCol = headers.length;

  titleBanner(ws, lastCol, 'Cotisations — Daara Wakana', `${contributions.length} cotisation(s) · Généré le ${new Date().toLocaleDateString('fr-FR')}`);
  tableHeader(ws, 4, headers);

  let r = 5;
  let totalConfirmed = 0;
  contributions.forEach((c, i) => {
    const status = c.status === 'confirmed' || c.status === 'rejected' ? c.status : 'pending';
    if (status === 'confirmed') totalConfirmed += c.amount || 0;
    const values = [
      fmtDate(c.createdAt),
      c.userName || 'Anonyme',
      c.type || '-',
      PAYMENT_LABELS[c.paymentMethod] || 'Espèces',
      c.amount || 0,
      STATUS_LABELS[status] || 'En attente',
      c.validatedBy || '-',
      c.validatedAt ? fmtDate(c.validatedAt) : '-'
    ];
    const row = ws.getRow(r);
    values.forEach((v, c2) => {
      const cell = row.getCell(c2 + 1);
      cell.value = v;
      cell.border = allBorders;
    });
    row.getCell(5).numFmt = MONEY_FMT;
    const statusCell = row.getCell(6);
    statusCell.alignment = { horizontal: 'center' };
    statusCell.font = { bold: true, color: { argb: STATUS_COLORS[status] || AMBER } };
    if (i % 2 === 1) {
      for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
    }
    r++;
  });

  // Ligne de total (cotisations validées)
  const totalRow = ws.getRow(r);
  totalRow.getCell(4).value = 'TOTAL VALIDÉ';
  totalRow.getCell(5).value = totalConfirmed;
  totalRow.getCell(5).numFmt = MONEY_FMT;
  for (let c2 = 1; c2 <= lastCol; c2++) {
    totalRow.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    totalRow.getCell(c2).font = { bold: true, color: { argb: GREEN } };
    totalRow.getCell(c2).border = { top: { style: 'medium', color: { argb: GREEN } }, bottom: thin, left: thin, right: thin };
  }

  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastCol } };
}

// ---- Feuille « Dépenses » ----

function buildExpensesSheet(wb, expenses, catLabel) {
  const ws = wb.addWorksheet('Dépenses', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  const headers = ['Date', 'Catégorie', 'Description', 'Montant', 'Enregistré par'];
  const widths = [14, 18, 44, 16, 20];
  ws.columns = widths.map((w) => ({ width: w }));
  const lastCol = headers.length;

  titleBanner(ws, lastCol, 'Dépenses — Daara Wakana', `${expenses.length} dépense(s) · Généré le ${new Date().toLocaleDateString('fr-FR')}`);
  tableHeader(ws, 4, headers, RED);

  let r = 5;
  let total = 0;
  expenses.forEach((e, i) => {
    total += e.amount || 0;
    const values = [
      fmtDate(e.createdAt) || e.date || '',
      catLabel(e.category),
      e.description || '-',
      e.amount || 0,
      e.createdBy || 'Admin'
    ];
    const row = ws.getRow(r);
    values.forEach((v, c2) => {
      const cell = row.getCell(c2 + 1);
      cell.value = v;
      cell.border = allBorders;
    });
    row.getCell(4).numFmt = MONEY_FMT;
    row.getCell(4).font = { color: { argb: RED } };
    if (i % 2 === 1) {
      for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
    }
    r++;
  });

  const totalRow = ws.getRow(r);
  totalRow.getCell(3).value = 'TOTAL DÉPENSES';
  totalRow.getCell(4).value = total;
  totalRow.getCell(4).numFmt = MONEY_FMT;
  for (let c2 = 1; c2 <= lastCol; c2++) {
    totalRow.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RED_LIGHT } };
    totalRow.getCell(c2).font = { bold: true, color: { argb: RED } };
    totalRow.getCell(c2).border = { top: { style: 'medium', color: { argb: RED } }, bottom: thin, left: thin, right: thin };
  }

  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastCol } };
}

// ---- Feuille « Recettes par type » ----

function buildTypeSheet(wb, typeBreakdown, stats) {
  const ws = wb.addWorksheet('Recettes par type', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'portrait' }
  });
  const headers = ['Type', 'Validé', 'En attente', 'Nb validées', '% des recettes'];
  ws.columns = [{ width: 28 }, { width: 18 }, { width: 18 }, { width: 14 }, { width: 16 }];
  const lastCol = headers.length;

  titleBanner(ws, lastCol, 'Recettes par type — Daara Wakana', `Généré le ${new Date().toLocaleDateString('fr-FR')}`);
  tableHeader(ws, 4, headers, GREEN);

  let r = 5;
  let totalConfirmed = 0;
  let totalPending = 0;
  const totalIncome = stats?.totalIncome || 0;
  typeBreakdown.forEach((t, i) => {
    totalConfirmed += t.confirmed || 0;
    totalPending += t.pending || 0;
    const pct = totalIncome > 0 ? ((t.confirmed || 0) / totalIncome) * 100 : 0;
    const row = ws.getRow(r);
    row.getCell(1).value = t.label;
    row.getCell(2).value = t.confirmed || 0;
    row.getCell(3).value = t.pending || 0;
    row.getCell(4).value = t.confirmedCount || 0;
    row.getCell(5).value = pct;
    row.getCell(2).numFmt = MONEY_FMT;
    row.getCell(3).numFmt = MONEY_FMT;
    row.getCell(5).numFmt = PCT_FMT;
    row.getCell(2).font = { color: { argb: GREEN } };
    row.getCell(3).font = { color: { argb: AMBER } };
    row.getCell(4).alignment = { horizontal: 'center' };
    row.getCell(5).alignment = { horizontal: 'center' };
    for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).border = allBorders;
    if (i % 2 === 1) for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
    r++;
  });

  const totalRow = ws.getRow(r);
  totalRow.getCell(1).value = 'TOTAL';
  totalRow.getCell(2).value = totalConfirmed;
  totalRow.getCell(3).value = totalPending;
  totalRow.getCell(2).numFmt = MONEY_FMT;
  totalRow.getCell(3).numFmt = MONEY_FMT;
  for (let c2 = 1; c2 <= lastCol; c2++) {
    totalRow.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    totalRow.getCell(c2).font = { bold: true, color: { argb: GREEN } };
    totalRow.getCell(c2).border = { top: { style: 'medium', color: { argb: GREEN } }, bottom: thin, left: thin, right: thin };
  }
}

// ---- Feuille « Par membre » ----

function buildMembersSheet(wb, memberBreakdown) {
  const ws = wb.addWorksheet('Par membre', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  const headers = ['#', 'Membre', 'Validé', 'En attente', 'Nb validées'];
  ws.columns = [{ width: 6 }, { width: 32 }, { width: 18 }, { width: 18 }, { width: 14 }];
  const lastCol = headers.length;

  titleBanner(ws, lastCol, 'Cotisations par membre — Daara Wakana', `${memberBreakdown.length} contributeur(s) · Généré le ${new Date().toLocaleDateString('fr-FR')}`);
  tableHeader(ws, 4, headers, BLUE);

  let r = 5;
  let totalConfirmed = 0;
  let totalPending = 0;
  memberBreakdown.forEach((m, i) => {
    totalConfirmed += m.confirmed || 0;
    totalPending += m.pending || 0;
    const row = ws.getRow(r);
    row.getCell(1).value = i + 1;
    row.getCell(2).value = m.name;
    row.getCell(3).value = m.confirmed || 0;
    row.getCell(4).value = m.pending || 0;
    row.getCell(5).value = m.confirmedCount || 0;
    row.getCell(1).alignment = { horizontal: 'center' };
    row.getCell(3).numFmt = MONEY_FMT;
    row.getCell(4).numFmt = MONEY_FMT;
    row.getCell(3).font = { color: { argb: GREEN } };
    row.getCell(4).font = { color: { argb: AMBER } };
    row.getCell(5).alignment = { horizontal: 'center' };
    for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).border = allBorders;
    if (i % 2 === 1) for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
    r++;
  });

  const totalRow = ws.getRow(r);
  totalRow.getCell(2).value = 'TOTAL';
  totalRow.getCell(3).value = totalConfirmed;
  totalRow.getCell(4).value = totalPending;
  totalRow.getCell(3).numFmt = MONEY_FMT;
  totalRow.getCell(4).numFmt = MONEY_FMT;
  for (let c2 = 1; c2 <= lastCol; c2++) {
    totalRow.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    totalRow.getCell(c2).font = { bold: true, color: { argb: GREEN } };
    totalRow.getCell(c2).border = { top: { style: 'medium', color: { argb: BLUE } }, bottom: thin, left: thin, right: thin };
  }

  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastCol } };
}

// ---- Feuille « Évolution mensuelle » ----

function buildMonthlySheet(wb, monthlyData) {
  const ws = wb.addWorksheet('Évolution mensuelle', { pageSetup: { orientation: 'portrait' } });
  const headers = ['Mois', 'Recettes', 'Dépenses', 'Solde'];
  ws.columns = [{ width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }];
  const lastCol = headers.length;

  titleBanner(ws, lastCol, 'Évolution mensuelle', '12 derniers mois · cotisations validées uniquement');
  tableHeader(ws, 4, headers, BLUE);

  let r = 5;
  let totalIncome = 0;
  let totalExpense = 0;
  // Ordre chronologique (du plus ancien au plus récent)
  const ordered = [...monthlyData];
  ordered.forEach((m, i) => {
    totalIncome += m.income || 0;
    totalExpense += m.expense || 0;
    const solde = (m.income || 0) - (m.expense || 0);
    const row = ws.getRow(r);
    row.getCell(1).value = `${m.name} ${m.year}`;
    row.getCell(2).value = m.income || 0;
    row.getCell(3).value = m.expense || 0;
    row.getCell(4).value = solde;
    row.getCell(2).numFmt = MONEY_FMT;
    row.getCell(3).numFmt = MONEY_FMT;
    row.getCell(4).numFmt = MONEY_FMT;
    row.getCell(2).font = { color: { argb: GREEN } };
    row.getCell(3).font = { color: { argb: RED } };
    row.getCell(4).font = { bold: true, color: { argb: solde >= 0 ? GREEN : RED } };
    for (let c2 = 1; c2 <= lastCol; c2++) row.getCell(c2).border = allBorders;
    if (i % 2 === 1) {
      for (let c2 = 1; c2 <= lastCol; c2++) {
        const cell = row.getCell(c2);
        if (!cell.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
      }
    }
    r++;
  });

  const totalRow = ws.getRow(r);
  totalRow.getCell(1).value = 'TOTAL';
  totalRow.getCell(2).value = totalIncome;
  totalRow.getCell(3).value = totalExpense;
  totalRow.getCell(4).value = totalIncome - totalExpense;
  [2, 3, 4].forEach((c2) => { totalRow.getCell(c2).numFmt = MONEY_FMT; });
  for (let c2 = 1; c2 <= lastCol; c2++) {
    totalRow.getCell(c2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    totalRow.getCell(c2).font = { bold: true, color: { argb: GREEN } };
    totalRow.getCell(c2).border = { top: { style: 'medium', color: { argb: BLUE } }, bottom: thin, left: thin, right: thin };
  }
}
